import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/core';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui-kit/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { ATTENDANCE_RULES, AttendanceRule, BreakTier } from '@/constant/hr/attendance-rule-catalog';
import { SoonPanel } from '../../../../components/soon-panel/soon-panel';

/** "5:30" for 19800 seconds. */
export const hoursOf = (seconds: number) => {
  const minutes = Math.round(seconds / 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
};

export interface BreakRow {
  /** Worked time per day, as a translation key with its values. */
  worked: { key: string; values: Record<string, string> };
  /** Minimum break in minutes; 0 for none. */
  minBreakMinutes: number;
}

/**
 * The rows of a break rule: "Up to <first threshold>" with no minimum, then one row per tier ("5:30–7:00 h",
 * "Over 9:00 h"), straight from the catalog so the page shows what the attendance workflows enforce.
 */
export const breakRowsOf = (tiers: BreakTier[]): BreakRow[] => {
  const sorted = [...tiers].sort((a, b) => a.aboveWorkSeconds - b.aboveWorkSeconds);
  if (!sorted.length) return [];
  return [
    {
      worked: { key: 'WORKED_UP_TO', values: { to: hoursOf(sorted[0].aboveWorkSeconds) } },
      minBreakMinutes: 0,
    },
    ...sorted.map(
      (tier): BreakRow => ({
        worked:
          tier.upToWorkSeconds === undefined
            ? { key: 'WORKED_OVER', values: { from: hoursOf(tier.aboveWorkSeconds) } }
            : {
                key: 'WORKED_BETWEEN',
                values: {
                  from: hoursOf(tier.aboveWorkSeconds),
                  to: hoursOf(tier.upToWorkSeconds),
                },
              },
        minBreakMinutes: Math.round(tier.minBreakSeconds / 60),
      })
    ),
  ];
};

/** The break rules of the catalog, in its order. */
const BREAK_RULES = ATTENDANCE_RULES.filter(
  (rule): rule is AttendanceRule<'breakTiers'> => rule.group === 'breakTiers'
);

/** Each break rule's title: its minimums, e.g. "Breaks of 15, 30 or 60 minutes". */
const titleValues = (tiers: BreakTier[]) => {
  const minutes = tiers.map((tier) => String(Math.round(tier.minBreakSeconds / 60)));
  return { list: minutes.slice(0, -1).join(', '), last: minutes[minutes.length - 1] };
};

/**
 * Settings › Time & Attendance › Attendance Rules (read-only, 3 Nov 2026): break time by working hours, one
 * table per break rule of the attendance rule catalog. A rule binds the roles that hold its permission (the
 * catalog depends on no country), so each table names that permission. The other rules come later.
 */
export const AttendanceRulesPage = () => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader title={t('ATTENDANCE_RULES')} description={t('ATTENDANCE_RULES_DESCRIPTION')} />

      <div className="grid gap-4 min-[1180px]:grid-cols-2">
        {BREAK_RULES.map((rule) => {
          const { list, last } = titleValues(rule.params.tiers);
          return (
            <Card key={rule.code} className="shadow-none">
              <CardHeader className="gap-1">
                <CardTitle className="text-[15px]">
                  {list ? t('BREAK_RULE_TITLE', { list, last }) : t('BREAK_RULE_TITLE_ONE', { last })}
                </CardTitle>
                <CardDescription>
                  {t('BREAK_RULE_APPLIES_TO')}{' '}
                  <code className="break-all text-xs">{rule.permission}</code>
                </CardDescription>
              </CardHeader>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-6">{t('WORKED_TIME_PER_DAY')}</TableHead>
                    <TableHead className="pr-6 text-right">{t('MINIMUM_BREAK')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakRowsOf(rule.params.tiers).map((row) => (
                    <TableRow key={row.worked.key + JSON.stringify(row.worked.values)}>
                      <TableCell className="pl-6 tabular-nums">
                        {t(row.worked.key, row.worked.values)}
                      </TableCell>
                      <TableCell className="pr-6 text-right tabular-nums">
                        {row.minBreakMinutes
                          ? t('BREAK_MINUTES', { minutes: String(row.minBreakMinutes) })
                          : t('NO_MINIMUM_BREAK')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          );
        })}
      </div>

      <SoonPanel name="OTHER_ATTENDANCE_RULES" />
    </div>
  );
};
